"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe, MapPin, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const nationalFeatures = ["Targeted search visibility across a single country within one primary language environment", "Streamlined technical site architecture with unified domain signals and single search index focus", "Competitor benchmarking against domestic rivals without cross-border overlap", "Consistent search intent aligned with domestic terminology and local user expectations", "Unified search console reporting and direct tracking without multi-region segmentation"];
const internationalFeatures = ["Multi-market and multi-language targeting layered onto the site foundation", "Hreflang implementation to signal language and regional page alternatives to search engines", "Strategic URL architecture decisions across ccTLDs, subdirectories, or subdomains", "Market-specific keyword research adapted to regional terminology, dialect, and differing intent", "Duplicate content management and canonicalisation across international variants", "Region-specific authority building and segmented performance tracking per country"];
export default function SeoComparison() {
  return <section id="internationalvsnationalseocomparison" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="outline" className="mb-4 border-border text-foreground font-medium">
            Strategic Scope &amp; Complexity
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-serif mb-6">
            International SEO vs National SEO
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When I evaluate global search opportunities, I do not treat international SEO as an entirely separate discipline. It builds directly upon the same technical, content, and authority fundamentals as a strong domestic strategy—while introducing layers for multiple languages, distinct regional SERPs, hreflang, URL architecture, and differing search intent.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-12">
          {/* National SEO Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="flex">
            <Card className="w-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
              <div>
                <CardHeader className="pb-4">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <MapPin className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-semibold text-foreground">
                    National SEO
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground pt-1">
                    Focused visibility across a single country within one primary language environment.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    With a focused domestic scope,{" "}
                    <Link to="/NationalSeoPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                      National SEO
                    </Link>{" "}
                    concentrates technical authority and search relevance into winning the search landscape of a single country. It does not carry the complexity of multi-language rendering, regional SERPs, or geopolitical URL routing.
                  </p>

                  <div className="space-y-3 pt-2">
                    {nationalFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground/90">
                        <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="p-6 pt-0 mt-6 border-t border-border/60">
                <p className="text-xs text-muted-foreground">
                  Best for businesses serving one territory without the requirement for localized currency, regional legal frameworks, or multi-lingual search indexes. For regional proximity and community visibility, explore{" "}
                  <Link to="/LocalSEOPage" className="text-primary underline underline-offset-2 hover:text-primary/80">
                    Local SEO
                  </Link>
                  .
                </p>
              </div>
            </Card>
          </motion.div>

          {/* International SEO Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="flex">
            <Card className="w-full flex flex-col justify-between border-primary/20 bg-card shadow-sm hover:shadow-md transition-shadow duration-300 relative">
              <div className="absolute -top-3 right-6">
                <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full shadow-xs">
                  Multi-Market Architecture
                </span>
              </div>

              <div>
                <CardHeader className="pb-4">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <Globe className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-semibold text-foreground">
                    International SEO
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground pt-1">
                    Multi-territory and multi-lingual architecture built upon core search foundations.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Rather than reinventing SEO from scratch, international execution scales national fundamentals across distinct regional Google search results, managing content localisation, language attribution, duplicate variants, and regional search intent differences.
                  </p>

                  <div className="space-y-3 pt-2">
                    {internationalFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground/90">
                        <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="p-6 pt-0 mt-6 border-t border-border/60">
                <p className="text-xs text-muted-foreground">
                  Essential for brands targeting cross-border traffic, localized ecommerce storefronts, or global enterprise expansion across Europe, the Middle East, and beyond.
                </p>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Foundation First Note */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="rounded-xl border border-border bg-card p-6 sm:p-8 text-center max-w-4xl mx-auto">
          <h3 className="text-lg sm:text-xl font-semibold text-foreground font-serif mb-2">
            When to Fix National Foundations First
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            If your domestic organic foundation has unresolved technical debt, thin content, or weak indexing, expanding into multiple overseas territories often compounds those issues. In those situations, the right strategic advice is to strengthen your national SEO foundations before committing budget to international markets.
          </p>
        </motion.div>
      </div>
    </section>;
}